import { useState, useEffect, useRef } from "react";
import { useAuthSession } from "./useAuthSession";
import { useNavigation } from "./useNavigation";
import { usePermissions } from "./usePermissions";
import { useTenantsData } from "./useTenantsData";
import { usePropertiesData } from "./usePropertiesData";
import { usePaymentsData } from "./usePaymentsData";
import { useMaintenanceData } from "./useMaintenanceData";
import { useExpensesData } from "./useExpensesData";
import { useCommunicationsData } from "./useCommunicationsData";
import { useDocumentsData } from "./useDocumentsData";
import { useSettingsData } from "./useSettingsData";
import { useTeamData } from "./useTeamData";
import { useLeasesData } from "./useLeasesData";
import { useUnitsData } from "./useUnitsData";

export function useAppData(supabase) {
  const { session, authLoading } = useAuthSession(supabase);
  const { page, setPage, showMoreMenu, setShowMoreMenu } = useNavigation(session);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState("admin");
  const [landlordId, setLandlordId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));

  // loadAll is assembled below (after the domain hooks it depends on), but the
  // domain hooks need a stable "reload" callback to hand to their handlers now.
  // A ref sidesteps the chicken-and-egg ordering problem.
  const loadAllRef = useRef(async () => {});
  const reload = () => loadAllRef.current();

  const tenantsData = useTenantsData(supabase, landlordId, reload);
  const propertiesData = usePropertiesData(supabase, landlordId, tenantsData.tenants, reload);
  const settingsData = useSettingsData(supabase, landlordId, reload);
  const paymentsData = usePaymentsData(supabase, landlordId, session, tenantsData.tenants, settingsData.settings, reload);
  const maintenanceData = useMaintenanceData(supabase, landlordId, session, reload);
  const expensesData = useExpensesData(supabase, landlordId, reload);
  const communicationsData = useCommunicationsData(supabase, landlordId, session, reload);
  const documentsData = useDocumentsData(supabase, landlordId, reload);
  const teamData = useTeamData(supabase, landlordId, reload);
  const leasesData = useLeasesData(supabase, reload);
  const unitsData = useUnitsData();
  const { can, visibleNav } = usePermissions(userRole);

  const loadAll = async () => {
    const uid   = session?.user?.id;
    const email = session?.user?.email;
    if (!uid) return;

    // Skip data loading for the admin panel — it fetches its own data via Edge Function.
    if (window.location.pathname.startsWith("/admin")) {
      setLoading(false);
      return;
    }

    // Tenants have their own portal with separate data fetching (supabase.rpc calls).
    // Skip all landlord/team-member data loading for them.
    if (session?.user?.user_metadata?.role === "tenant") {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Resolve landlord/team-member scope server-side via get-owner-context,
      // which looks up team_members by the caller's verified JWT (service
      // role, no RLS needed). Do NOT read landlord_id/role from
      // user_metadata — it's client-writable via supabase.auth.updateUser()
      // and must never be trusted to decide whose data to load.
      const { data: ownerCtx, error: ownerCtxErr } = await supabase.functions.invoke("get-owner-context");
      if (ownerCtxErr) throw ownerCtxErr;

      const ownerId = ownerCtx?.owner_id ?? uid;
      const role    = ownerCtx?.member_role || "admin";

      setUserRole(role);
      setLandlordId(ownerId);

      const [t, p, m, e, c, docs, ltmpls, sData, tm, pe, propData, photosData, riData, cpData, alData, leaseData, unitsResult] = await Promise.all([
        supabase.from("tenants").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("payments").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("maintenance").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("expenses").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("communications").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("documents").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("letter_templates").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("settings").select("*").eq("user_id", ownerId).single(),
        supabase.from("team_members").select("*").eq("landlord_id", ownerId).order("created_at", { ascending: false }),
        supabase.from("payment_edits").select("*").eq("edited_by", email).order("edited_at", { ascending: false }),
        supabase.from("properties").select("*").eq("user_id", ownerId).order("created_at", { ascending: true }),
        supabase.from("tenant_photos").select("*").eq("user_id", ownerId).order("uploaded_at", { ascending: false }),
        supabase.from("rent_increases").select("*").eq("user_id", ownerId).order("effective_date", { ascending: true }),
        supabase.from("company_profiles").select("*").eq("user_id", ownerId).maybeSingle(),
        supabase.from("payment_audit_log").select("*").eq("user_id", ownerId).order("changed_at", { ascending: false }),
        supabase.from("leases").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
        // No user_id column on units — scoped entirely by RLS via property_id -> properties.user_id.
        supabase.from("units").select("*"),
      ]);

      console.log("[loadAll] tenants   :", { count: t.data?.length, error: t.error?.message });
      console.log("[loadAll] payments  :", { count: p.data?.length, error: p.error?.message });
      console.log("[loadAll] properties:", { count: propData.data?.length, error: propData.error?.message });
      console.log("[loadAll] settings  :", { data: sData.data, error: sData.error?.message });
      console.log("=== [loadAll] END ===");

      if (t.error) throw t.error;
      if (p.error) throw p.error;
      if (m.error) throw m.error;
      if (e.error) throw e.error;

      tenantsData.setTenants(t.data || []);
      paymentsData.setPayments(p.data || []);
      maintenanceData.setMaintenance(m.data || []);
      expensesData.setExpenses(e.data || []);
      communicationsData.setCommunications(c.data || []);
      documentsData.setSavedDocs(docs.data || []);
      documentsData.setCustomTemplates(ltmpls.data || []);
      const rawSettings = sData.data || {};
      const metaName  = session?.user?.user_metadata?.display_name || session?.user?.user_metadata?.full_name || "";
      const metaEmail = session?.user?.email || "";
      if (!sData.data) {
        const seedPayload = { user_id: ownerId, landlordName: metaName, landlordEmail: metaEmail };
        const { data: newRow } = await supabase.from("settings").insert([seedPayload]).select().single();
        if (newRow) settingsData.setSettings(s => ({ ...s, ...newRow }));
      } else {
        settingsData.setSettings(s => ({
          ...s,
          ...rawSettings,
          landlordName:  rawSettings.landlordName  || metaName,
          landlordEmail: rawSettings.landlordEmail || metaEmail,
        }));
      }
      teamData.setTeamMembers(tm.data || []);
      paymentsData.setPaymentEdits(pe.data || []);
      if (!propData.error) propertiesData.setProperties(propData.data || []);
      if (!photosData.error) tenantsData.setTenantPhotos(photosData.data || []);
      if (!riData.error) tenantsData.setRentIncreases(riData.data || []);
      if (cpData && !cpData.error && cpData.data) settingsData.setCompanyProfile(cp => ({ ...cp, ...cpData.data }));
      paymentsData.setPaymentAuditLog(alData?.data || []);
      if (!leaseData.error) leasesData.setLeases(leaseData.data || []);
      if (!unitsResult.error) unitsData.setUnits(unitsResult.data || []);
      if (!riData.error) {
        const today = new Date().toISOString().slice(0, 10);
        const due = (riData.data || []).filter(ri => !ri.applied && ri.effective_date <= today);
        if (due.length > 0) {
          const now = new Date().toISOString();
          const freshTenants = t.data || [];
          for (const ri of due) {
            const tenant = freshTenants.find(ten => ten.name === ri.tenant);
            if (!tenant) continue;
            await supabase.from("tenants").update({ rent: ri.new_rent }).eq("id", tenant.id);
            await supabase.from("rent_increases").update({ applied: true, applied_at: now }).eq("id", ri.id);
            await supabase.from("rent_increase_audit").insert([{
              user_id: ownerId, tenant: ri.tenant,
              old_rent: tenant.rent, new_rent: ri.new_rent,
              effective_date: ri.effective_date, applied_at: now, method: "auto",
            }]);
          }
          const [tFresh, riFresh] = await Promise.all([
            supabase.from("tenants").select("*").eq("user_id", ownerId).order("created_at", { ascending: false }),
            supabase.from("rent_increases").select("*").eq("user_id", ownerId).order("effective_date", { ascending: true }),
          ]);
          if (!tFresh.error) tenantsData.setTenants(tFresh.data || []);
          if (!riFresh.error) tenantsData.setRentIncreases(riFresh.data || []);
        }
      }
    } catch (err) {
      setError("Could not connect to database: " + err.message);
    }
    setLoading(false);
  };
  loadAllRef.current = loadAll;

  useEffect(() => { if (session) loadAll(); }, [session]);
  useEffect(() => { if (page !== "Tenants") tenantsData.setSelectedTenant(null); }, [page]);

  // ── DERIVED VALUES ───────────────────────────────────────────────────────────
  const { tenants } = tenantsData;
  const { payments } = paymentsData;
  const { expenses } = expensesData;
  const { maintenance } = maintenanceData;

  const totalRent     = tenants.reduce((s, t) => s + (t.rent || 0), 0);
  const collected     = payments.filter(p => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
  const overdueAmt    = payments.filter(p => p.status === "overdue").reduce((s, p) => s + (p.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const openTickets   = maintenance.filter(m => m.status !== "resolved").length;
  const todayDate     = new Date();
  const in60          = new Date(); in60.setDate(todayDate.getDate() + 60);
  const expiringLeases = tenants.filter(t => {
    if (!t.lease_end) return false;
    const end = new Date(t.lease_end);
    return end >= todayDate && end <= in60;
  });

  return {
    // session / auth
    session, authLoading,
    // navigation
    page, setPage, visibleNav, showMoreMenu, setShowMoreMenu,
    // data
    tenants, payments, maintenance, expenses, communications: communicationsData.communications,
    properties: propertiesData.properties, units: unitsData.units, tenantPhotos: tenantsData.tenantPhotos,
    savedDocs: documentsData.savedDocs, customTemplates: documentsData.customTemplates,
    paymentEdits: paymentsData.paymentEdits, paymentAuditLog: paymentsData.paymentAuditLog,
    teamMembers: teamData.teamMembers, settings: settingsData.settings, setSettings: settingsData.setSettings,
    rentIncreases: tenantsData.rentIncreases,
    leases: leasesData.leases,
    // derived
    totalRent, collected, overdueAmt, totalExpenses, openTickets, expiringLeases,
    // ui state
    loading, error, userRole, landlordId, can,
    // tenant ui state
    searchTenant: tenantsData.searchTenant, setSearchTenant: tenantsData.setSearchTenant,
    selectedTenant: tenantsData.selectedTenant, setSelectedTenant: tenantsData.setSelectedTenant,
    showAddTenant: tenantsData.showAddTenant, setShowAddTenant: tenantsData.setShowAddTenant,
    newTenant: tenantsData.newTenant, setNewTenant: tenantsData.setNewTenant,
    editTenant: tenantsData.editTenant, setEditTenant: tenantsData.setEditTenant,
    showTenantHistory: tenantsData.showTenantHistory, setShowTenantHistory: tenantsData.setShowTenantHistory,
    photoType: tenantsData.photoType, setPhotoType: tenantsData.setPhotoType,
    photoLabel: tenantsData.photoLabel, setPhotoLabel: tenantsData.setPhotoLabel,
    photoUploadFile: tenantsData.photoUploadFile, setPhotoUploadFile: tenantsData.setPhotoUploadFile,
    photoUploading: tenantsData.photoUploading,
    // payment ui state
    collapsedMonths: paymentsData.collapsedMonths, setCollapsedMonths: paymentsData.setCollapsedMonths,
    showAddPayment: paymentsData.showAddPayment, setShowAddPayment: paymentsData.setShowAddPayment,
    newPayment: paymentsData.newPayment, setNewPayment: paymentsData.setNewPayment,
    newPaymentError: paymentsData.newPaymentError, setNewPaymentError: paymentsData.setNewPaymentError,
    quickPayError: paymentsData.quickPayError, setQuickPayError: paymentsData.setQuickPayError,
    showEditPayment: paymentsData.showEditPayment, setShowEditPayment: paymentsData.setShowEditPayment,
    editPaymentDraft: paymentsData.editPaymentDraft, setEditPaymentDraft: paymentsData.setEditPaymentDraft,
    editPaymentError: paymentsData.editPaymentError, setEditPaymentError: paymentsData.setEditPaymentError,
    editInternalNotes: paymentsData.editInternalNotes, setEditInternalNotes: paymentsData.setEditInternalNotes,
    expandedPaymentEdits: paymentsData.expandedPaymentEdits, setExpandedPaymentEdits: paymentsData.setExpandedPaymentEdits,
    invoiceView: paymentsData.invoiceView, setInvoiceView: paymentsData.setInvoiceView,
    invoiceModal: paymentsData.invoiceModal, setInvoiceModal: paymentsData.setInvoiceModal,
    invoiceSending: paymentsData.invoiceSending, invoiceResult: paymentsData.invoiceResult, setInvoiceResult: paymentsData.setInvoiceResult,
    sendVia: paymentsData.sendVia, setSendVia: paymentsData.setSendVia,
    // maintenance ui state
    showAddMaint: maintenanceData.showAddMaint, setShowAddMaint: maintenanceData.setShowAddMaint,
    newMaint: maintenanceData.newMaint, setNewMaint: maintenanceData.setNewMaint,
    viewMaint: maintenanceData.viewMaint, setViewMaint: maintenanceData.setViewMaint,
    editMaintMode: maintenanceData.editMaintMode, setEditMaintMode: maintenanceData.setEditMaintMode,
    editMaintData: maintenanceData.editMaintData, setEditMaintData: maintenanceData.setEditMaintData,
    showResolveMaint: maintenanceData.showResolveMaint, setShowResolveMaint: maintenanceData.setShowResolveMaint,
    resolveDate: maintenanceData.resolveDate, setResolveDate: maintenanceData.setResolveDate,
    maintNotes: maintenanceData.maintNotes, maintNoteInput: maintenanceData.maintNoteInput,
    setMaintNoteInput: maintenanceData.setMaintNoteInput, postingMaintNote: maintenanceData.postingMaintNote,
    postMaintNote: maintenanceData.postMaintNote,
    // expense ui state
    showAddExpense: expensesData.showAddExpense, setShowAddExpense: expensesData.setShowAddExpense,
    newExpense: expensesData.newExpense, setNewExpense: expensesData.setNewExpense,
    // communications ui state
    showCommLog: communicationsData.showCommLog, setShowCommLog: communicationsData.setShowCommLog,
    showAddComm: communicationsData.showAddComm, setShowAddComm: communicationsData.setShowAddComm,
    newComm: communicationsData.newComm, setNewComm: communicationsData.setNewComm,
    commSearch: communicationsData.commSearch, setCommSearch: communicationsData.setCommSearch,
    commCatFilter: communicationsData.commCatFilter, setCommCatFilter: communicationsData.setCommCatFilter,
    commPriFilter: communicationsData.commPriFilter, setCommPriFilter: communicationsData.setCommPriFilter,
    commTenantFilter: communicationsData.commTenantFilter, setCommTenantFilter: communicationsData.setCommTenantFilter,
    commDateFrom: communicationsData.commDateFrom, setCommDateFrom: communicationsData.setCommDateFrom,
    commDateTo: communicationsData.commDateTo, setCommDateTo: communicationsData.setCommDateTo,
    // documents ui state
    docsTab: documentsData.docsTab, setDocsTab: documentsData.setDocsTab,
    showDocModal: documentsData.showDocModal, setShowDocModal: documentsData.setShowDocModal,
    selectedTemplate: documentsData.selectedTemplate,
    docTenant: documentsData.docTenant, setDocTenant: documentsData.setDocTenant,
    docExtras: documentsData.docExtras, setDocExtras: documentsData.setDocExtras,
    docBody: documentsData.docBody, setDocBody: documentsData.setDocBody,
    docSubject: documentsData.docSubject, setDocSubject: documentsData.setDocSubject,
    docSaved: documentsData.docSaved,
    viewSavedDoc: documentsData.viewSavedDoc, setViewSavedDoc: documentsData.setViewSavedDoc,
    showNewTemplate: documentsData.showNewTemplate, setShowNewTemplate: documentsData.setShowNewTemplate,
    newTemplate: documentsData.newTemplate, setNewTemplate: documentsData.setNewTemplate,
    editTemplate: documentsData.editTemplate, setEditTemplate: documentsData.setEditTemplate,
    // settings ui state
    settingsSaved: settingsData.settingsSaved,
    // company profile ui state
    companyProfile: settingsData.companyProfile, setCompanyProfile: settingsData.setCompanyProfile,
    companyProfileSaved: settingsData.companyProfileSaved, logoSaved: settingsData.logoSaved,
    saveCompanyProfile: settingsData.saveCompanyProfile,
    // financials ui state
    selectedYear, setSelectedYear,
    // team ui state
    showAddMember: teamData.showAddMember, setShowAddMember: teamData.setShowAddMember,
    newMember: teamData.newMember, setNewMember: teamData.setNewMember,
    // properties ui state
    showAddProperty: propertiesData.showAddProperty, setShowAddProperty: propertiesData.setShowAddProperty,
    newProperty: propertiesData.newProperty, setNewProperty: propertiesData.setNewProperty,
    editProperty: propertiesData.editProperty, setEditProperty: propertiesData.setEditProperty,
    propTab: propertiesData.propTab, setPropTab: propertiesData.setPropTab,
    selectedProperty: propertiesData.selectedProperty, setSelectedProperty: propertiesData.setSelectedProperty,
    // rent increases ui state
    showAddRentIncrease: tenantsData.showAddRentIncrease, setShowAddRentIncrease: tenantsData.setShowAddRentIncrease,
    newRentIncrease: tenantsData.newRentIncrease, setNewRentIncrease: tenantsData.setNewRentIncrease,
    // lease generator ui state
    showLeaseWizard: leasesData.showLeaseWizard, setShowLeaseWizard: leasesData.setShowLeaseWizard,
    leaseActionPending: leasesData.leaseActionPending,
    leaseActionError: leasesData.leaseActionError, setLeaseActionError: leasesData.setLeaseActionError,
    submitLease: leasesData.submitLease, getLeasePdfUrl: leasesData.getLeasePdfUrl,
    checkLeaseConflict: leasesData.checkLeaseConflict,
    // handlers
    loadAll,
    addTeamMember: teamData.addTeamMember, resendInvite: teamData.resendInvite,
    removeTeamMember: teamData.removeTeamMember, updateMemberRole: teamData.updateMemberRole,
    saveSettings: settingsData.saveSettings,
    addTenant: tenantsData.addTenant, saveTenantEdit: tenantsData.saveTenantEdit, deleteTenant: tenantsData.deleteTenant,
    uploadTenantPhoto: tenantsData.uploadTenantPhoto, deleteTenantPhoto: tenantsData.deleteTenantPhoto,
    addPayment: paymentsData.addPayment, savePaymentEdit: paymentsData.savePaymentEdit,
    deletePayment: paymentsData.deletePayment, changePaymentStatus: paymentsData.changePaymentStatus,
    handleMarkPaid: paymentsData.handleMarkPaid, quickCreatePayment: paymentsData.quickCreatePayment,
    openInvoice: paymentsData.openInvoice, sendInvoice: paymentsData.sendInvoice,
    autoGeneratePayments: paymentsData.autoGeneratePayments, runLateFeeCheck: paymentsData.runLateFeeCheck,
    addMaintenance: maintenanceData.addMaintenance, deleteMaintenance: maintenanceData.deleteMaintenance,
    updateMaintFull: maintenanceData.updateMaintFull, saveMaintenanceEdit: maintenanceData.saveMaintenanceEdit,
    updateMaintStatus: maintenanceData.updateMaintStatus,
    addExpense: expensesData.addExpense, deleteExpense: expensesData.deleteExpense,
    addComm: communicationsData.addComm, deleteComm: communicationsData.deleteComm,
    saveDocument: documentsData.saveDocument, deleteDocument: documentsData.deleteDocument,
    saveCustomTemplate: documentsData.saveCustomTemplate, updateCustomTemplate: documentsData.updateCustomTemplate,
    deleteCustomTemplate: documentsData.deleteCustomTemplate, openDocModal: documentsData.openDocModal,
    seedDefaultProperties: propertiesData.seedDefaultProperties, addProperty: propertiesData.addProperty,
    savePropertyEdit: propertiesData.savePropertyEdit, deleteProperty: propertiesData.deleteProperty,
    syncPropertiesFromTenants: propertiesData.syncPropertiesFromTenants,
    addRentIncrease: tenantsData.addRentIncrease, applyRentIncrease: tenantsData.applyRentIncrease,
    deleteRentIncrease: tenantsData.deleteRentIncrease,
  };
}
